import React, { useCallback, useEffect, useState } from "react";
import api from "../api/api";
import Swal from "sweetalert2";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { motion, AnimatePresence } from "framer-motion";
import CustomSelect from "../components/CustomSelect";
import useAutoRefresh from "../hooks/useAutoRefresh";
import { buildPaginationItems } from "../utils/pagination";
import {
  Scissors,
  Search,
  X,
  Edit,
  Trash2,
  Plus,
  Save,
  ToggleRight,
  ToggleLeft,
} from "lucide-react";
import { IMaskInput } from "react-imask";

/** Máscara de moeda pt-BR: 1.234,56 */
const mascaraPrecoBRL = {
  mask: Number,
  scale: 2,
  thousandsSeparator: ".",
  radix: ",",
  mapToRadix: ["."],
  normalizeZeros: true,
  padFractionalZeros: true,
  min: 0,
};

const parsePreco = (value) => {
  if (value === "" || value == null) return null;
  const n = Number(String(value).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const Servicos = () => {
  // Funções do Drawer e ações
  const abrirDrawer = (servico) => {
    setServicoSelecionado(servico);
    setDrawerOpen(true);
  };

  const fecharDrawer = () => {
    setDrawerOpen(false);
    setServicoSelecionado(null);
  };

  const deletarServico = async (id) => {
    try {
      await api.delete(`/servicos/${id}`);
      toast.success('Serviço excluído!');
      carregarServicos();
      fecharDrawer();
    } catch (err) {
      console.error('Erro ao excluir serviço:', err);
      toast.error('Erro ao excluir serviço!');
    }
  };

  const confirmarExcluir = (id, nome) => {
    const nomeExibicao = (nome || '').toString().toUpperCase();
    Swal.fire({
      title: 'Confirma exclusão?',
      html: `Deseja realmente excluir o serviço <strong>${nomeExibicao}</strong>? Esta ação não pode ser desfeita.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33',
    }).then((result) => {
      if (result.isConfirmed) {
        deletarServico(id);
      }
    });
  };

  const salvarAlteracoes = async () => {
    if (!servicoSelecionado) return;
    try {
      await api.put(`/servicos/${servicoSelecionado.id}`, {
        ...servicoSelecionado,
        preco: parsePreco(servicoSelecionado.preco),
        duracao_minutos: servicoSelecionado.duracao_minutos
          ? parseInt(servicoSelecionado.duracao_minutos, 10)
          : null,
      });
      toast.success('Serviço atualizado com sucesso!');
      carregarServicos();
      fecharDrawer();
    } catch (err) {
      console.error('Erro ao atualizar serviço:', err);
      toast.error('Erro ao atualizar serviço!');
    }
  };
  const [servicos, setServicos] = useState([]);
  const [busca, setBusca] = useState("");
  const [statusFiltro, setStatusFiltro] = useState("todos");
  const [loading, setLoading] = useState(true);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(10);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [servicoSelecionado, setServicoSelecionado] = useState(null);
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");

  // Modal Novo Serviço
  const [showNovo, setShowNovo] = useState(false);
  const [novoLoading, setNovoLoading] = useState(false);
  const [optsLoading, setOptsLoading] = useState(false);
  const [estabelecimentos, setEstabelecimentos] = useState([]);
  const [novoServico, setNovoServico] = useState({
    estabelecimento_id: "",
    nome: "",
    descricao: "",
    preco: "",
    duracao_minutos: "",
    ativo: true,
  });
  const [erros, setErros] = useState({});

  // Spinner
  const Spinner = ({ size = 16 }) => (
    <span
      style={{
        display: "inline-block",
        width: size,
        height: size,
        border: "2px solid rgba(0,0,0,0.15)",
        borderTopColor: "var(--primary)",
        borderRadius: "50%",
        animation: "spin 0.6s linear infinite",
      }}
    />
  );

  const isAtivo = (item) => item?.ativo === true || item?.ativo === 1 || item?.ativo === "1";

  const formatCurrency = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return "Não informado";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(number);
  };

  const formatDuration = (value) => {
    const minutes = Number(value);
    if (!Number.isFinite(minutes) || minutes <= 0) return "Não informado";
    return `${minutes} min`;
  };

  // CSS keyframes
  useEffect(() => {
    if (document.getElementById("spin-keyframes")) return;
    const style = document.createElement("style");
    style.id = "spin-keyframes";
    style.innerHTML = `
      @keyframes spin { to { transform: rotate(360deg); } }
      .input-error { border: 1px solid #ef4444 !important; box-shadow: 0 0 0 3px rgba(239,68,68,.15); }
      .error-text { color: #ef4444; font-size: 12px; margin-top: 6px; }
      .modal-title { font-weight: 800; font-size: 18px; }
      .modal-subtitle { color: var(--muted); font-size: 13px; margin-top: 4px; }
      .field-label { font-weight: 700; font-size: 13px; margin-bottom: 6px; display:block; }
      .field { width: 100%; padding: 10px 12px; border: 1px solid var(--line); border-radius: 10px; background: var(--card); color: var(--text); }
      .field:focus { outline: none; box-shadow: 0 0 0 3px rgba(37,99,235,.15); border-color: var(--primary); }
      .modal-card { width: 680px; max-width: calc(100vw - 32px); background: var(--card); border-radius: 16px; box-shadow: var(--shadow); }
      .modal-actions { display:flex; gap: 10px; justify-content: flex-end; padding-top: 6px; }
      .btn-secondary { border: 1px solid var(--line); background: #f3f4f6; color: var(--text); border-radius: 10px; padding: 10px 14px; font-weight: 600; cursor: pointer; }
      .btn-secondary:hover { background: #e5e7eb; }
      .section-split { height: 1px; background: var(--line); margin: 12px 0; }
      .two-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
      .three-cols { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; }
      /* toggle button styles moved to src/index.css to ensure consistent loading */
      .price-badge { background: #dcfce7; color: #166534; padding: 6px 12px; border-radius: 8px; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; font-size: 13px; }
      .duration-badge { background: #dbeafe; color: #1e40af; padding: 6px 12px; border-radius: 8px; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; font-size: 13px; }
      @media (max-width: 1200px) {
        .table { font-size: 13px; }
        .table th, .table td { padding: 10px 8px; }
      }
      @media (max-width: 768px) {
        .table-responsive { overflow-x: auto; }
        .table { min-width: 900px; }
        .two-cols, .three-cols { grid-template-columns: 1fr !important; }
      }
    `;
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isDark = document.body.classList.contains("dark");
      setTheme(isDark ? "dark" : "light");
    });
    observer.observe(document.body, { attributes: true });
    return () => observer.disconnect();
  }, []);

  const carregarServicos = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = {
        page: pagina,
        limit: itensPorPagina,
        busca,
        status: statusFiltro !== 'todos' ? statusFiltro : undefined,
      };
      // ... requisição usando params ...
      // Exemplo:
      const { data } = await api.get('/servicos', { params });
      setServicos(data.resultados || []);
      setTotalPaginas(data.paginas || 1);
    } catch (err) {
      console.error('Erro ao carregar serviços:', err);
      if (!silent) toast.error('Erro ao carregar serviços!');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [pagina, itensPorPagina, busca, statusFiltro]);

  useEffect(() => {
    carregarServicos(false);
  }, [pagina, itensPorPagina, busca, statusFiltro, carregarServicos]);

  useEffect(() => {
    const handler = () => carregarServicos(true);
    window.addEventListener("fz:agendamento", handler);
    window.addEventListener("fz:notificacao", handler);
    return () => {
      window.removeEventListener("fz:agendamento", handler);
      window.removeEventListener("fz:notificacao", handler);
    };
  }, [carregarServicos]);

  useAutoRefresh(
    () => carregarServicos(true),
    10000,
    !drawerOpen && !showNovo && !optsLoading && !novoLoading
  );

  // Paginação e lista paginada já declaradas no topo do componente
  const paginationItems = buildPaginationItems(pagina, totalPaginas);


  // ========== NOVO SERVIÇO ==========
  const abrirModalNovo = async () => {
    setErros({});
    setNovoServico({
      estabelecimento_id: "",
      nome: "",
      descricao: "",
      preco: "",
      duracao_minutos: "",
      ativo: true,
    });
    // prefill estabelecimento_id from current logged-in estabelecimento (if available)
    const estId = localStorage.getItem('estabelecimento_id');
    if (estId) {
      setNovoServico((s) => ({ ...s, estabelecimento_id: String(estId) }));
    }
    setShowNovo(true);
    await carregarOpcoesSelects();
  };

  const fecharModalNovo = () => {
    setShowNovo(false);
  };

  const normalizaResultado = (data) =>
    Array.isArray(data) ? data : data?.resultados || [];

  const carregarOpcoesSelects = async () => {
    try {
      setOptsLoading(true);
      const { data } = await api.get("/estabelecimentos");
      setEstabelecimentos(normalizaResultado(data));
    } catch (e) {
      console.error("Erro ao carregar opções:", e);
      toast.error("Erro ao carregar estabelecimentos.");
    } finally {
      setOptsLoading(false);
    }
  };

  const validarNovo = () => {
    const e = {};
    if (!novoServico.nome?.trim()) e.nome = "O nome é obrigatório";
    if (!novoServico.estabelecimento_id) e.estabelecimento_id = "Selecione um estabelecimento";
    const duracao = parseInt(novoServico.duracao_minutos, 10);
    if (!Number.isFinite(duracao) || duracao <= 0) {
      e.duracao_minutos = "Informe a duração em minutos";
    }
    setErros(e);
    return Object.keys(e).length === 0;
  };

  const salvarNovoServico = async () => {
    if (!validarNovo()) {
      toast.warn("Revise os campos obrigatórios.");
      return;
    }
    setNovoLoading(true);
    try {
      await api.post("/servicos", {
        estabelecimento_id: Number(novoServico.estabelecimento_id),
        nome: novoServico.nome.trim(),
        descricao: novoServico.descricao?.trim() || null,
        preco: parsePreco(novoServico.preco),
        duracao_minutos: parseInt(novoServico.duracao_minutos, 10),
        ativo: novoServico.ativo,
      });
      toast.success("Serviço criado com sucesso!");
      setShowNovo(false);
      carregarServicos();
    } catch (e) {
      console.error("Erro ao criar serviço:", e);
      toast.error(e?.response?.data?.mensagem || "Erro ao criar serviço.");
    } finally {
      setNovoLoading(false);
    }
  };

  const errClass = (field) => (erros[field] ? "field input-error" : "field");

  const renderServicoInfo = (serv) => (
    <div className="entity-cell">
      <span className="entity-cell__title">{serv.nome || "-"}</span>
      <span className="entity-cell__subtitle">
        {serv.descricao || serv.estabelecimento?.nome || "Sem descrição cadastrada"}
      </span>
    </div>
  );

  const renderServicoAcoes = (serv) => (
    <div className="table-actions table-actions--compact">
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.97 }}
        className="action-icon-btn action-icon-btn--primary"
        onClick={() => abrirDrawer(serv)}
        title="Editar serviço"
      >
        <Edit size={18} /> <span className="action-label">Editar</span>
      </motion.button>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.97 }}
        className="action-icon-btn action-icon-btn--danger"
        onClick={() => confirmarExcluir(serv.id, serv.nome)}
        title="Excluir serviço"
      >
        <Trash2 size={18} /> <span className="action-label">Excluir</span>
      </motion.button>
    </div>
  );

  return (
    <div className="container fade-in">
      <ToastContainer
        position="top-right"
        theme={theme === "dark" ? "dark" : "colored"}
      />

      <div
        className="section-title flex items-center gap-2"
        style={{ justifyContent: "space-between" }}
      >
        <div className="flex items-center gap-2">
          <Scissors /> <span>Serviços</span>
        </div>

        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          className="btn"
          onClick={abrirModalNovo}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 14px",
            borderRadius: 10,
            fontWeight: 700,
          }}
        >
          <Plus /> Novo Serviço
        </motion.button>
      </div>

      {/* FILTROS */}
      <motion.div
        className="card-block space-b management-filters"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="management-filters__title">Filtrar serviços</div>
        <div className="filters-row">
          <div
            className="flex items-center filter-field"
            style={{
              background: "var(--card)",
              border: "1px solid var(--line)",
              borderRadius: "10px",
              padding: "6px 10px",
              boxShadow: "var(--shadow)",
            }}
          >
            <Search style={{ color: "var(--muted)", marginRight: "8px" }} />
            <input
              type="text"
              placeholder="Buscar por nome, descrição ou estabelecimento..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              style={{
                width: "100%",
                border: "none",
                outline: "none",
                background: "transparent",
                color: "var(--text)",
              }}
            />
          </div>
          <CustomSelect
            value={statusFiltro}
            onChange={(e) => setStatusFiltro(e.target.value)}
            options={[
              { value: "todos", label: "Todos os status" },
              { value: "ativo", label: "Ativos" },
              { value: "inativo", label: "Inativos" },
            ]}
            style={{ minWidth: "150px" }}
          />
          <CustomSelect
            value={itensPorPagina}
            onChange={(e) => {
              setPagina(1);
              setItensPorPagina(Number(e.target.value));
            }}
            options={[
              { value: 5, label: "5 por página" },
              { value: 10, label: "10 por página" },
              { value: 20, label: "20 por página" },
            ]}
            style={{ minWidth: "150px" }}
          />
        </div>
      </motion.div>

      {/* LISTAGEM */}
      <motion.div
        className="card-block"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {loading ? (
          <p
            className="muted"
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            <Spinner /> Carregando serviços...
          </p>
        ) : servicos.length === 0 ? (
          <div className="empty-state empty-state--compact">
            <Scissors size={34} />
            <strong>Nenhum serviço encontrado</strong>
            <span>Cadastre serviços para liberar horários e agendamentos.</span>
          </div>
        ) : (
          <>
            <div className="management-mobile-list">
              <div className="management-mobile-list__head">
                <span>Serviço</span>
                <span>Ações</span>
              </div>
              {servicos.map((serv) => (
                <div key={serv.id} className="management-mobile-row">
                  <div className="management-mobile-row__main">{renderServicoInfo(serv)}</div>
                  <div className="management-mobile-row__actions">{renderServicoAcoes(serv)}</div>
                </div>
              ))}
            </div>

            <div className="management-table-desktop table-responsive">
              <table className="table table--mobile-actions">
                <thead>
                  <tr>
                    <th>Serviço</th>
                    <th>Preço</th>
                    <th>Duração</th>
                    <th>Status</th>
                    <th className="management-actions-cell">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {servicos.map((serv) => (
                    <motion.tr
                      key={serv.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <td>{renderServicoInfo(serv)}</td>
                      <td>
                        <span className="meta-pill">{formatCurrency(serv.preco)}</span>
                      </td>
                      <td>
                        <span className="meta-pill">{formatDuration(serv.duracao_minutos)}</span>
                      </td>
                      <td>
                        <span className={`status-pill ${isAtivo(serv) ? "status-pill--active" : "status-pill--inactive"}`}>
                          {isAtivo(serv) ? "Ativo" : "Inativo"}
                        </span>
                      </td>
                      <td className="management-actions-cell">{renderServicoAcoes(serv)}</td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Paginação - sempre exibe os botões se houver mais de 1 item */}
            {servicos.length > 0 && (
              <div className="pagination-bar pagination-bar--spaced">
                <button
                  className="btn-secondary"
                  disabled={pagina === 1}
                  onClick={() => setPagina(pagina - 1)}
                  style={{ minWidth: 60, borderRadius: 8, fontWeight: 600, fontSize: 15, opacity: pagina === 1 ? 0.6 : 1 }}
                >Anterior</button>
                {paginationItems.map((item, index) => {
                  if (typeof item !== "number") {
                    return (
                      <span key={`${item}-${index}`} className="pagination-ellipsis" aria-hidden="true">
                        ...
                      </span>
                    );
                  }
                  return (
                    <button
                      key={item}
                      className={`btn-secondary${pagina === item ? ' active' : ''}`}
                      style={{
                        minWidth: 40,
                        borderRadius: 8,
                        fontWeight: pagina === item ? 700 : 500,
                        fontSize: 15,
                        background: pagina === item ? 'var(--primary-light)' : undefined,
                        color: pagina === item ? 'var(--primary)' : undefined,
                        boxShadow: pagina === item ? '0 2px 8px rgba(37,99,235,0.08)' : undefined,
                        border: pagina === item ? '2px solid var(--primary)' : '1px solid var(--line)',
                        transition: 'all 0.2s',
                      }}
                      onClick={() => setPagina(item)}
                    >
                      {item}
                    </button>
                  );
                })}
                <button
                  className="btn-secondary"
                  disabled={pagina === totalPaginas}
                  onClick={() => setPagina(pagina + 1)}
                  style={{ minWidth: 60, borderRadius: 8, fontWeight: 600, fontSize: 15, opacity: pagina === totalPaginas ? 0.6 : 1 }}
                >Próxima</button>
              </div>
            )}
          </>
        )}
      </motion.div>

      {/* DRAWER (edição) */}
      <AnimatePresence>
        {drawerOpen && servicoSelecionado && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={fecharDrawer}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.4)",
                backdropFilter: "blur(4px)",
                zIndex: 50,
              }}
            />
            <motion.div
              className="drawer-panel"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 280, damping: 30 }}
              style={{ zIndex: 60 }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <h3 style={{ fontWeight: "700", fontSize: "20px" }}>
                  Editar Serviço
                </h3>
                <button
                  onClick={fecharDrawer}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "var(--muted)",
                    fontSize: "18px",
                  }}
                >
                  <X />
                </button>
              </div>

              <div style={{ marginTop: "20px" }}>
                <label className="field-label">Nome do Serviço *</label>
                <input
                  type="text"
                  className="field"
                  placeholder="Ex: Corte de Cabelo"
                  value={servicoSelecionado.nome || ""}
                  onChange={(e) =>
                    setServicoSelecionado({
                      ...servicoSelecionado,
                      nome: e.target.value,
                    })
                  }
                />

                <label className="field-label" style={{ marginTop: 12 }}>
                  Descrição
                </label>
                <textarea
                  rows={3}
                  className="field"
                  placeholder="Descrição do serviço..."
                  value={servicoSelecionado.descricao || ""}
                  onChange={(e) =>
                    setServicoSelecionado({
                      ...servicoSelecionado,
                      descricao: e.target.value,
                    })
                  }
                />

                <label className="field-label" style={{ marginTop: 12 }}>
                  Estabelecimento
                </label>
                <input
                  type="text"
                  className="field"
                  value={servicoSelecionado.estabelecimento?.nome || "-"}
                  disabled
                  style={{ background: "#f3f4f6", cursor: "not-allowed" }}
                />

                <label className="field-label" style={{ marginTop: 12 }}>
                  Preço (R$)
                </label>
                <IMaskInput
                  {...mascaraPrecoBRL}
                  className="field"
                  placeholder="Ex: 50,00"
                  inputMode="decimal"
                  value={
                    servicoSelecionado.preco === null || servicoSelecionado.preco === undefined
                      ? ""
                      : String(servicoSelecionado.preco)
                  }
                  unmask
                  onAccept={(value) =>
                    setServicoSelecionado({
                      ...servicoSelecionado,
                      preco: value,
                    })
                  }
                />

                <label className="field-label" style={{ marginTop: 12 }}>
                  Duração (minutos)
                </label>
                <input
                  type="number"
                  className="field"
                  placeholder="Ex: 30"
                  value={servicoSelecionado.duracao_minutos || ""}
                  onChange={(e) =>
                    setServicoSelecionado({
                      ...servicoSelecionado,
                      duracao_minutos: e.target.value,
                    })
                  }
                />

                <label className="field-label" style={{ marginTop: 12 }}>
                  Status
                </label>
                <button
                  className={`toggle-btn ${
                    servicoSelecionado.ativo ? "toggle-ativo" : "toggle-inativo"
                  }`}
                  onClick={() =>
                    setServicoSelecionado({
                      ...servicoSelecionado,
                      ativo: !servicoSelecionado.ativo,
                    })
                  }
                >
                  {servicoSelecionado.ativo ? (
                    <>
                      <ToggleRight /> Ativo
                    </>
                  ) : (
                    <>
                      <ToggleLeft /> Inativo
                    </>
                  )}
                </button>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap", marginTop: "22px" }}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                  className="btn hover-glow"
                  onClick={salvarAlteracoes}
                >
                  <Save style={{ marginRight: 6 }} />
                  Salvar Alterações
                </motion.button>
                <button className="btn-secondary" onClick={fecharDrawer}>
                  Fechar
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* MODAL NOVO SERVIÇO */}
      <AnimatePresence>
        {showNovo && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={fecharModalNovo}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.40)",
                backdropFilter: "blur(3px)",
                zIndex: 70,
              }}
            />
            <motion.div
              className="modal-shell"
              initial={{ scale: 0.98, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.98, opacity: 0, y: 10 }}
              transition={{ duration: 0.2 }}
            >
              <div className="modal-card modal-card--form">
                <div className="modal-card__header">
                  <div>
                    <div className="modal-title">Novo Serviço</div>
                    <div className="modal-subtitle">
                      Preencha os campos abaixo para cadastrar.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={fecharModalNovo}
                    className="modal-close-btn"
                    aria-label="Fechar"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="modal-card__scroll">
                {optsLoading ? (
                  <p
                    className="muted"
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <Spinner /> Carregando opções...
                  </p>
                ) : (
                  <div className="form-field-stack">
                    <div>
                      <label className="field-label">Estabelecimento *</label>
                      {localStorage.getItem("estabelecimento_id") ? (
                        <input
                          type="text"
                          className="field field-readonly"
                          value={
                            estabelecimentos.find(
                              (e) =>
                                String(e.id) === String(localStorage.getItem("estabelecimento_id"))
                            )?.nome ||
                            `Estabelecimento #${localStorage.getItem("estabelecimento_id")}`
                          }
                          disabled
                          readOnly
                        />
                      ) : (
                        <CustomSelect
                          value={novoServico.estabelecimento_id}
                          autoSelectSingle={false}
                          onChange={(e) =>
                            setNovoServico((s) => ({
                              ...s,
                              estabelecimento_id: e.target.value,
                            }))
                          }
                          options={[
                            { value: "", label: "Selecione..." },
                            ...estabelecimentos.map((est) => ({
                              value: est.id,
                              label: est.nome || `Estabelecimento #${est.id}`,
                            })),
                          ]}
                          className={erros.estabelecimento_id ? "input-error" : ""}
                        />
                      )}
                      {erros.estabelecimento_id && (
                        <div className="error-text">{erros.estabelecimento_id}</div>
                      )}
                    </div>

                    <div>
                      <label className="field-label">Nome do Serviço *</label>
                      <input
                        type="text"
                        className={errClass("nome")}
                        placeholder="Ex: Corte de Cabelo, Manicure..."
                        value={novoServico.nome}
                        onChange={(e) =>
                          setNovoServico((s) => ({
                            ...s,
                            nome: e.target.value,
                          }))
                        }
                      />
                      {erros.nome && <div className="error-text">{erros.nome}</div>}
                    </div>

                    <div>
                      <label className="field-label">Descrição</label>
                      <textarea
                        rows={3}
                        className="field"
                        placeholder="Descrição do serviço (opcional)..."
                        value={novoServico.descricao}
                        onChange={(e) =>
                          setNovoServico((s) => ({
                            ...s,
                            descricao: e.target.value,
                          }))
                        }
                      />
                    </div>

                    <div className="two-cols">
                      <div>
                        <label className="field-label">Preço (R$)</label>
                        <IMaskInput
                          {...mascaraPrecoBRL}
                          className="field"
                          placeholder="Ex: 50,00"
                          inputMode="decimal"
                          value={novoServico.preco}
                          unmask
                          onAccept={(value) =>
                            setNovoServico((s) => ({
                              ...s,
                              preco: value,
                            }))
                          }
                        />
                      </div>

                      <div>
                        <label className="field-label">Duração (minutos) *</label>
                        <input
                          type="number"
                          min="1"
                          className={errClass("duracao_minutos")}
                          placeholder="Ex: 30"
                          value={novoServico.duracao_minutos}
                          onChange={(e) =>
                            setNovoServico((s) => ({
                              ...s,
                              duracao_minutos: e.target.value,
                            }))
                          }
                        />
                        {erros.duracao_minutos && (
                          <div className="error-text">{erros.duracao_minutos}</div>
                        )}
                      </div>
                    </div>

                    <div className="field-row-toggle">
                      <div className="field-row-toggle__copy">
                        <div className="field-label">Status</div>
                        <div className="muted">Serviço disponível para agendamento.</div>
                      </div>
                      <button
                        type="button"
                        className={`toggle-btn ${
                          novoServico.ativo ? "toggle-ativo" : "toggle-inativo"
                        }`}
                        onClick={() =>
                          setNovoServico((s) => ({
                            ...s,
                            ativo: !s.ativo,
                          }))
                        }
                      >
                        {novoServico.ativo ? (
                          <>
                            <ToggleRight /> Ativo
                          </>
                        ) : (
                          <>
                            <ToggleLeft /> Inativo
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
                </div>

                {!optsLoading && (
                  <div className="modal-card__footer modal-actions">
                    <button className="btn-secondary" onClick={fecharModalNovo}>
                      Cancelar
                    </button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="btn"
                      disabled={novoLoading}
                      onClick={salvarNovoServico}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      {novoLoading ? <Spinner /> : <Plus />}
                      Criar Serviço
                    </motion.button>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Servicos;
